import { Auth0Client } from '@auth0/auth0-spa-js'
import type { Auth0ClientOptions } from '@auth0/auth0-spa-js'
import type { IAuthService } from './auth.service.interface'
import type { User, LoginOptions, LogoutOptions, AuthResult } from '@/types/auth.types'
import { reportError } from '@/lib/error-reporting'

export class Auth0Service implements IAuthService {
  private auth0Client: Auth0Client

  constructor(config: Auth0ClientOptions) {
    this.auth0Client = new Auth0Client(config)
  }

  async initialize(): Promise<AuthResult<void>> {
    try {
      // Try to restore session from cache
      // This will silently fail if there's no valid session, which is fine
      // We don't want to trigger a redirect or show errors during initialization
      await this.auth0Client.checkSession()
      return { ok: true, value: undefined }
    } catch (error) {
      // Silently handle initialization errors
      // Common cases:
      // - No existing session (user needs to log in)
      // - Session expired (user needs to log in again)
      // - Invalid organization in cached session
      // - Network errors (will retry on next navigation)

      // Check if this is an organization-related error or other Auth0 error
      // These errors indicate corrupted/invalid cached session data
      const errorMessage = error instanceof Error ? error.message : String(error)
      const isInvalidSessionError =
        errorMessage.includes('organization') ||
        errorMessage.includes('Invalid state') ||
        errorMessage.includes('400') ||
        errorMessage.includes('invalid_request') ||
        errorMessage.includes('login_required')

      if (isInvalidSessionError) {
        // Clear the corrupted session cache
        // This forces a clean login on the next attempt
        try {
          await this.clearSessionCache()
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log('[Auth0Service] Cleared invalid session cache due to error:', errorMessage)
          }
        } catch (clearError) {
          // Ignore errors when clearing cache
          reportError(clearError, { where: 'auth0.service.initialize.clearSessionCache' })
        }
      } else if (import.meta.env.DEV) {
        // Only log in development to avoid console noise in production
        // eslint-disable-next-line no-console
        console.log(
          '[Auth0Service] Session check during initialization failed (this is normal for first-time visitors or expired sessions):',
          error
        )
      }

      // Return success even if session check fails
      // The user will be redirected to login by the auth guard if needed
      return { ok: true, value: undefined }
    }
  }

  /**
   * Clear Auth0 session cache from localStorage
   * This removes corrupted or invalid session data
   */
  private async clearSessionCache(): Promise<void> {
    // Auth0 SDK stores data in localStorage with keys matching the domain/clientId
    // We need to remove these to force a clean session
    const auth0Keys = Object.keys(localStorage).filter(
      key => key.startsWith('@@auth0spajs@@') || key.startsWith('a0.spajs')
    )

    auth0Keys.forEach(key => localStorage.removeItem(key))
  }

  async login(options?: LoginOptions): Promise<AuthResult<void>> {
    try {
      // Remember Me implementation:
      // - Session duration is controlled by max_age parameter (7 days) in Auth0 config
      // - Actual session expiration is enforced by timestamp checking in auth.store.ts
      // - When rememberMe is TRUE: Session valid for 7 days (timestamp check allows it)
      // - When rememberMe is FALSE: Session expires after 24 hours (timestamp check forces logout)
      //
      // We do NOT use prompt='login' as it can cause Auth0 to create very short sessions.
      // Instead, we rely on:
      // 1. Auth0's max_age parameter (7 days) for the authentication lifetime
      // 2. Our timestamp-based checks to enforce shorter durations when rememberMe=false
      // 3. Refresh tokens to keep the session alive within the allowed duration

      // Build authorizationParams only with defined values (exactOptionalPropertyTypes)
      const authorizationParams: Record<string, string> = {}
      if (options?.organization) authorizationParams.organization = options.organization
      if (options?.screenHint) authorizationParams.screen_hint = options.screenHint
      if (options?.loginHint) authorizationParams.login_hint = options.loginHint
      if (options?.connection) authorizationParams.connection = options.connection
      if (options?.invitation) authorizationParams.invitation = options.invitation

      await this.auth0Client.loginWithRedirect({
        appState: options?.appState,
        ...(Object.keys(authorizationParams).length > 0 && { authorizationParams }),
      })
      return { ok: true, value: undefined }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('Login failed'),
      }
    }
  }

  async logout(options?: LogoutOptions): Promise<AuthResult<void>> {
    try {
      const logoutParams: { returnTo?: string } = {}
      if (options?.returnTo) {
        logoutParams.returnTo = options.returnTo
      }

      await this.auth0Client.logout({
        logoutParams,
      })
      return { ok: true, value: undefined }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('Logout failed'),
      }
    }
  }

  async handleRedirectCallback(): Promise<AuthResult<{ appState?: unknown }>> {
    try {
      const result = await this.auth0Client.handleRedirectCallback()
      return { ok: true, value: result }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('Callback handling failed'),
      }
    }
  }

  async getAccessToken(): Promise<AuthResult<string>> {
    try {
      // Let the Auth0 SDK handle token caching and refresh automatically
      // It will use cached tokens when valid and refresh when needed
      const token = await this.auth0Client.getTokenSilently()

      return { ok: true, value: token }
    } catch (error) {
      // Check if this is a login_required error (session expired)
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase()
        if (
          errorMessage.includes('login_required') ||
          errorMessage.includes('consent_required') ||
          errorMessage.includes('interaction_required')
        ) {
          return {
            ok: false,
            error: new Error('Session expired. Please log in again.'),
          }
        }
      }

      return {
        ok: false,
        error: error instanceof Error ? error : new Error('Token retrieval failed'),
      }
    }
  }

  /**
   * Force a fresh token bypassing the local cache.
   * Passes cacheMode: 'off' so the Auth0 SDK skips the in-memory / localStorage
   * cache and goes directly to the refresh-token grant. Call this when the
   * current token is within the 5-minute expiry buffer but BEFORE Auth0's own
   * leeway (~60 s) removes the cached refresh token.
   *
   * IMPORTANT: This method first checks whether a refresh token is present in the
   * Auth0 localStorage cache. Calling getTokenSilently({ cacheMode: 'off' }) when
   * NO refresh token exists triggers silent iframe auth, which fails with
   * `login_required` and causes the Auth0 SDK to CLEAR the still-valid cache entry,
   * causing a premature sign-out. By checking first we avoid this side-effect.
   *
   * Refresh tokens are only issued when the Auth0 API has "Allow Offline Access"
   * enabled. If that is not configured, this method returns { ok: false } safely.
   */
  async forceRefreshToken(): Promise<AuthResult<string>> {
    try {
      // Peek at the Auth0 localStorage cache to determine whether a refresh token
      // is stored. getTokenSilently({ cacheMode: 'off' }) should ONLY be called
      // when a refresh token is available — calling it without one triggers silent
      // iframe authentication which, on failure (login_required), removes the
      // still-valid access-token cache entry and causes premature sign-out.
      if (!this.hasRefreshTokenInCache()) {
        return {
          ok: false,
          error: new Error(
            'No refresh token available — Auth0 API may not have offline_access enabled'
          ),
        }
      }

      const token = await this.auth0Client.getTokenSilently({ cacheMode: 'off' })
      return { ok: true, value: token }
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase()
        if (
          errorMessage.includes('login_required') ||
          errorMessage.includes('consent_required') ||
          errorMessage.includes('interaction_required')
        ) {
          return { ok: false, error: new Error('Session expired. Please log in again.') }
        }
      }
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('Forced token refresh failed'),
      }
    }
  }

  /**
   * Check whether the Auth0 localStorage cache contains a refresh token.
   * Auth0 SPA JS caches refresh tokens in the same localStorage entry as the
   * access token under the key format:
   * @@auth0spajs@@::<clientId>::<audience>::<scope>
   */
  private hasRefreshTokenInCache(): boolean {
    try {
      // Check ALL Auth0 cache entries (there can be multiple for different audience/scope
      // combinations). A refresh token in any of them means we can attempt a forced refresh.
      const cacheKeys = Object.keys(localStorage).filter(
        k => k.startsWith('@@auth0spajs@@') && !k.endsWith('@@user@@')
      )
      return cacheKeys.some(key => {
        try {
          const cached = JSON.parse(localStorage.getItem(key) ?? '{}')
          return !!cached?.body?.refresh_token
        } catch {
          return false
        }
      })
    } catch {
      return false
    }
  }

  async getIdToken(): Promise<AuthResult<string>> {
    try {
      const claims = await this.auth0Client.getIdTokenClaims()
      if (!claims || !claims.__raw) {
        return {
          ok: false,
          error: new Error('ID token not found'),
        }
      }
      return { ok: true, value: claims.__raw }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('ID token retrieval failed'),
      }
    }
  }

  async getUser(): Promise<AuthResult<User | null>> {
    try {
      const auth0User = await this.auth0Client.getUser()
      if (!auth0User) {
        return { ok: true, value: null }
      }

      const user: User = {
        id: auth0User.sub || '',
        sub: auth0User.sub || '',
        email: auth0User.email || '',
        email_verified: auth0User.email_verified || false,
      }

      // Add optional fields only if they exist
      if (auth0User.name) {
        user.name = auth0User.name
      }
      if (auth0User.nickname) {
        user.nickname = auth0User.nickname
      }
      if (auth0User.picture) {
        user.picture = auth0User.picture
      }
      const rawRoles =
        (auth0User['https://sitecompass.au/roles'] as string[] | undefined) ||
        (auth0User['https://sitecompass.com/roles'] as string[] | undefined)
      if (rawRoles) {
        // Normalise Auth0 org role names to internal role identifiers
        const normalised = rawRoles.flatMap(r =>
          r === 'Organisation Administrator' ? [r, 'admin'] : [r]
        )
        user.roles = [...new Set(normalised)]
      }
      const rawPermissions =
        (auth0User['https://sitecompass.au/permissions'] as string[] | undefined) ||
        (auth0User['https://sitecompass.com/permissions'] as string[] | undefined)
      if (rawPermissions) {
        user.permissions = rawPermissions
      }

      return { ok: true, value: user }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('User retrieval failed'),
      }
    }
  }

  async isAuthenticated(): Promise<AuthResult<boolean>> {
    try {
      const authenticated = await this.auth0Client.isAuthenticated()
      return { ok: true, value: authenticated }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('Authentication check failed'),
      }
    }
  }

  async checkSession(): Promise<AuthResult<void>> {
    try {
      await this.auth0Client.checkSession()
      return { ok: true, value: undefined }
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error : new Error('Session check failed'),
      }
    }
  }
}
